// src/pages/Users/Messaging/CreateMessageGroupForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAllGroupsMessage } from "../../../services/groupServices";
import { sendGroupMessage } from "../../../services/messageServices";
import { IoIosSend } from "react-icons/io";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const quillFormats = ["header", "bold", "italic", "underline", "list", "bullet", "link", "clean"];

const SUBJECT_OPTIONS = [
  "Adhésion",
  "Dossier d'inscription",
  "Règlement",
  "Informations cours",
  "Absence",
  "Inscription compétition",
  "Perte d'objet",
  "Autre (saisir)",
];

export default function CreateMessageGroupForm({ userId, onHide, onSent }) {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [sending, setSending] = useState(false);

  const [groupId, setGroupId] = useState("");
  const [subjectChoice, setSubjectChoice] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const fetched = await getAllGroupsMessage();
        if (!cancelled) setGroups(Array.isArray(fetched) ? fetched : []);
      } catch (e) {
        console.error("Erreur groupes:", e);
        toast.error("Erreur lors de la récupération des groupes.");
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };
    fetchGroups();
    return () => {
      cancelled = true;
    };
  }, []);

  const canSubmit = useMemo(() => {
    const finalSubject =
      subjectChoice === "Autre (saisir)" ? customSubject.trim() : subjectChoice.trim();
    return !!groupId && !!finalSubject && !!content;
  }, [groupId, subjectChoice, customSubject, content]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Veuillez renseigner le groupe, l'objet et le contenu.");
      return;
    }
    try {
      setSending(true);
      const finalSubject =
        subjectChoice === "Autre (saisir)" ? customSubject.trim() : subjectChoice.trim();
      await sendGroupMessage({
        sender_id: userId,
        group_id: groupId,
        subject: finalSubject,
        content,
      });
      toast.success("Message groupé envoyé avec succès.");
      onSent?.();
      onHide?.();
    } catch (error) {
      console.error("Envoi message groupé:", error);
      toast.error("Erreur lors de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/70 backdrop-blur">
      {/* Pas de header dégradé ici — juste une petite étiquette sobre */}
      <div className="px-4 pt-4">
        <span className="inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 ring-1 ring-slate-200 dark:ring-slate-700">
          Nouveau message groupé
        </span>
      </div>

      <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
        {/* Groupe */}
        <div>
          <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
            Groupe
          </label>
          {loadingGroups ? (
            <div className="h-10 w-full rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse" />
          ) : (
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            >
              <option value="">Choisissez un groupe</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.nom}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Objet & “Autre” */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
              Objet
            </label>
            <select
              value={subjectChoice}
              onChange={(e) => setSubjectChoice(e.target.value)}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            >
              <option value="">Sélectionnez une option</option>
              {SUBJECT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {subjectChoice === "Autre (saisir)" && (
            <div>
              <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                Saisissez votre objet
              </label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Ex. : Rappel, info spéciale…"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          )}
        </div>

        {/* Contenu */}
        <div>
          <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
            Contenu
          </label>
          <div className="rounded-xl overflow-hidden ring-1 ring-slate-300 dark:ring-slate-700">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="Rédigez votre message à destination du groupe…"
              className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Actions compactes */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onHide}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <FaTimes className="text-[12px]" />
            Annuler
          </button>
          <Button
            type="submit"
            text={sending ? "Envoi…" : "Envoyer"}
            icon={IoIosSend}
            className="gap-2 !rounded-lg"
            disabled={sending || loadingGroups || !canSubmit}
          />
        </div>
      </form>
    </div>
  );
}
